/**
 * Google Ads Click-ID and UTM Parameter Tracking
 * Captures gclid, gbraid, wbraid and UTM parameters from URL
 * Stores them in localStorage with 90-day expiry
 */

const STORAGE_KEY = 'acad_tracking_params';
const EXPIRY_DAYS = 90;

/**
 * Get URL parameters from current page
 */
export const getURLTrackingParams = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      gclid: params.get('gclid'),
      gbraid: params.get('gbraid'),
      wbraid: params.get('wbraid'),
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_term: params.get('utm_term'),
      utm_content: params.get('utm_content'),
    };
  } catch (error) {
    console.error('Error reading URL parameters:', error);
    return {};
  }
};

/**
 * Get stored tracking parameters from localStorage
 */
export const getStoredTrackingParams = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);
    
    // Check if expired
    if (data.expires && new Date().getTime() > data.expires) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return data.params;
  } catch (error) {
    console.error('Error reading stored tracking params:', error);
    return null;
  }
};

/**
 * Save tracking parameters to localStorage
 * New gclid replaces old one, but never overwrite with empty value
 */
export const saveTrackingParams = (urlParams) => {
  try {
    const stored = getStoredTrackingParams() || {};
    
    // Merge with stored params, preferring new non-empty values
    const params = {
      gclid: urlParams.gclid || stored.gclid || null,
      gbraid: urlParams.gbraid || stored.gbraid || null,
      wbraid: urlParams.wbraid || stored.wbraid || null,
      utm_source: urlParams.utm_source || stored.utm_source || null,
      utm_medium: urlParams.utm_medium || stored.utm_medium || null,
      utm_campaign: urlParams.utm_campaign || stored.utm_campaign || null,
      utm_term: urlParams.utm_term || stored.utm_term || null,
      utm_content: urlParams.utm_content || stored.utm_content || null,
    };

    // Set expiry to 90 days from now
    const expires = new Date().getTime() + (EXPIRY_DAYS * 24 * 60 * 60 * 1000);

    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      params,
      expires,
      timestamp: new Date().toISOString()
    }));

    console.log('📊 Tracking params saved:', params);
    return params;
  } catch (error) {
    console.error('Error saving tracking params:', error);
    return null;
  }
};

/**
 * Get landing page URL (first page visited)
 */
export const getLandingPage = () => {
  try {
    const stored = localStorage.getItem('acad_landing_page');
    if (stored) return stored;

    // First visit - save current URL
    const landingPage = window.location.href;
    localStorage.setItem('acad_landing_page', landingPage);
    return landingPage;
  } catch (error) {
    console.error('Error getting landing page:', error);
    return window.location.href;
  }
};

/**
 * Initialize tracking on page load
 * Call this once when app loads
 */
export const initializeTracking = () => {
  try {
    const urlParams = getURLTrackingParams();
    
    // If any tracking params in URL, save them
    if (Object.values(urlParams).some(v => v !== null)) {
      saveTrackingParams(urlParams);
    }

    // Ensure landing page is captured
    getLandingPage();
  } catch (error) {
    console.error('Error initializing tracking:', error);
  }
};

/**
 * Get all tracking data to send with form submission
 */
export const getTrackingDataForSubmission = () => {
  try {
    const stored = getStoredTrackingParams() || {};
    const landing = getLandingPage();
    const current = window.location.href;

    return {
      gclid: stored.gclid || null,
      gbraid: stored.gbraid || null,
      wbraid: stored.wbraid || null,
      utm_source: stored.utm_source || null,
      utm_medium: stored.utm_medium || null,
      utm_campaign: stored.utm_campaign || null,
      utm_term: stored.utm_term || null,
      utm_content: stored.utm_content || null,
      landing_page: landing,
      page_url: current,
    };
  } catch (error) {
    console.error('Error getting tracking data:', error);
    return {
      gclid: null,
      gbraid: null,
      wbraid: null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_term: null,
      utm_content: null,
      landing_page: window.location.href,
      page_url: window.location.href,
    };
  }
};

export default {
  initializeTracking,
  getTrackingDataForSubmission,
  getURLTrackingParams,
  getStoredTrackingParams,
  saveTrackingParams,
  getLandingPage
};
